import * as React from "react";
import { css } from "@emotion/core";
import {
  Button,
  ButtonGroup,
  FormGroup,
  Icon,
  InputGroup,
  Popover,
  Spinner,
} from "@blueprintjs/core";
import { useEditor, EditorContent } from "@tiptap/react";
import type { Editor } from "@tiptap/core";
import type { EditorState } from "prosemirror-state";
import type { EditorView } from "prosemirror-view";
import StarterKit from "@tiptap/starter-kit";
import { Dropcursor } from "@tiptap/extensions";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { BubbleMenu, FloatingMenu } from "@tiptap/react/menus";
import { IKnowledgeBaseArticle } from "../../api/knowledgeBase";
import { slugify } from "../../utils/slug";
import ApiKnowledgeBase from "../../api/knowledgeBase";
import {
  replaceImageUrls,
  resolveImageUrlsToSigned,
  preloadImages,
  KNOWLEDGE_BASE_IMAGE_PREFIX,
  KNOWLEDGE_BASE_VIDEO_PREFIX,
} from "./utils/imageResolver";
import Image from "./extensions/Image";
import Video from "./extensions/Video";

interface ArticleViewerProps {
  article: IKnowledgeBaseArticle | null;
  isLoading: boolean;
  isCreating: boolean;
  isEditing: boolean;
  isFybrAdmin: boolean;
  editTitle: string;
  editSlug: string;
  setEditTitle: (value: string) => void;
  setEditSlug: (value: string) => void;
  onSave: (data: { title: string; slug: string; content: any }) => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
  isSaving: boolean;
  isDeleting: boolean;
}

const ArticleViewer: React.FC<ArticleViewerProps> = ({
  article,
  isLoading,
  isCreating,
  isEditing,
  isFybrAdmin,
  editTitle,
  editSlug,
  setEditTitle,
  setEditSlug,
  onSave,
  onStartEdit,
  onCancelEdit,
  onDelete,
  isSaving,
  isDeleting,
}) => {
  const initialContent = article?.content || {
    type: "doc",
    content: [{ type: "paragraph" }],
  };

  const normalizeUploadError = React.useCallback((error: unknown) => {
    if (error instanceof Error) {
      return error.message;
    }
    if (typeof error === "string") {
      return error;
    }
    return "Upload failed. Remove and try again.";
  }, []);

  const insertImageWithUpload = React.useCallback(
    (
      view: EditorView,
      file: File,
      insert: (view: EditorView, blobUrl: string) => number | null,
      options: { postInsert?: () => void } = {}
    ) => {
      const blobUrl = URL.createObjectURL(file);
      const provisionalPos = insert(view, blobUrl);

      if (options.postInsert) {
        options.postInsert();
      }

      const findImagePos = () => {
        if (provisionalPos !== null) {
          const directNode = view.state.doc.nodeAt(provisionalPos);
          if (
            directNode &&
            directNode.type === view.state.schema.nodes.image &&
            directNode.attrs?.src === blobUrl
          ) {
            return provisionalPos;
          }
        }

        let foundPos: number | null = null;
        view.state.doc.descendants((n, pos) => {
          if (
            n.type === view.state.schema.nodes.image &&
            n.attrs?.src === blobUrl
          ) {
            foundPos = pos;
            return false;
          }
          return true;
        });
        return foundPos;
      };

      const applyImageAttrs = (attrs: Record<string, unknown>) => {
        const imagePos = findImagePos();
        if (imagePos !== null) {
          const existing = view.state.doc.nodeAt(imagePos);
          const update = view.state.tr.setNodeMarkup(imagePos, undefined, {
            ...(existing?.attrs || {}),
            ...attrs,
          });
          view.dispatch(update);
        }
      };

      ApiKnowledgeBase.uploadImage(file)
        .then(({ url }) => {
          let swapped = false;
          const replaceWithUploaded = () => {
            if (swapped) return;
            const imagePos = findImagePos();
            if (imagePos !== null) {
              applyImageAttrs({ src: url, uploadError: null });
              URL.revokeObjectURL(blobUrl);
              swapped = true;
            }
          };

          const preload = new window.Image();
          preload.onload = replaceWithUploaded;
          preload.onerror = replaceWithUploaded;
          preload.src = url;

          window.setTimeout(replaceWithUploaded, 3000);
        })
        .catch((error) => {
          const message = normalizeUploadError(error);
          console.error("Image upload failed", error);
          applyImageAttrs({ src: "", uploadError: message });
          URL.revokeObjectURL(blobUrl);
        });
    },
    [normalizeUploadError]
  );

  const insertVideoWithUpload = React.useCallback(
    (
      view: EditorView,
      file: File,
      insert: (view: EditorView, node: any) => number | null,
      options: { postInsert?: () => void } = {}
    ) => {
      const videoNode = view.state.schema.nodes.video;
      if (!videoNode) {
        console.error("Video node is not registered in the editor schema");
        return;
      }

      const node = videoNode.create({
        src: "",
        controls: true,
        uploadError: null,
        uploading: true,
        uploadProgress: 0,
        uploadFilename: file.name,
      });

      const insertedPos = insert(view, node);

      if (options.postInsert) {
        options.postInsert();
      }

      const findVideoPos = () => {
        if (insertedPos !== null) {
          const directNode = view.state.doc.nodeAt(insertedPos);
          if (directNode && directNode.type === videoNode) {
            return insertedPos;
          }
        }

        let foundPos: number | null = null;
        view.state.doc.descendants((n, pos) => {
          if (
            n.type === videoNode &&
            (n.attrs?.uploading === true ||
              n.attrs?.uploadFilename === file.name)
          ) {
            foundPos = pos;
            return false;
          }
          return true;
        });
        return foundPos;
      };

      const applyVideoAttrs = (attrs: Record<string, unknown>) => {
        const videoPos = findVideoPos();
        if (videoPos !== null) {
          const existing = view.state.doc.nodeAt(videoPos);
          const update = view.state.tr.setNodeMarkup(videoPos, undefined, {
            ...(existing?.attrs || {}),
            ...attrs,
          });
          view.dispatch(update);
        }
      };

      ApiKnowledgeBase.uploadVideoWithProgress(file, (progress) => {
        applyVideoAttrs({ uploadProgress: progress, uploading: true });
      })
        .then(({ url }) => {
          applyVideoAttrs({
            src: url,
            controls: true,
            uploadError: null,
            uploading: false,
            uploadProgress: 1,
          });
        })
        .catch((error) => {
          const message = normalizeUploadError(error);
          console.error("Video upload failed", error);
          applyVideoAttrs({
            src: "",
            uploadError: message,
            controls: false,
            uploading: false,
            uploadProgress: 0,
          });
        });
    },
    [normalizeUploadError]
  );

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      Video,
      Underline,
      Link.configure({
        autolink: true,
        openOnClick: false,
        linkOnPaste: true,
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      Dropcursor,
    ],
    content: initialContent,
    editable: isFybrAdmin && isEditing,
    editorProps: {
      attributes: {
        class: "tiptap-editor",
        style:
          "color: var(--neutral-6); background-color: var(--neutral-1); height: 100%;",
      },
      handleDrop: function (view, event, slice, moved) {
        const file = event.dataTransfer?.files?.[0];
        if (!moved && file) {
          const coordinates = view.posAtCoords({
            left: event.clientX,
            top: event.clientY,
          });

          if (file.type.startsWith("image/")) {
            if (coordinates) {
              insertImageWithUpload(
                view,
                file,
                (currentView, currentBlobUrl) => {
                  const node = currentView.state.schema.nodes.image.create({
                    src: currentBlobUrl,
                  });
                  const transaction = currentView.state.tr.insert(
                    coordinates.pos,
                    node
                  );
                  currentView.dispatch(transaction);
                  return transaction.mapping.map(coordinates.pos);
                }
              );
            }
            return true;
          }

          if (file.type.startsWith("video/")) {
            if (coordinates) {
              insertVideoWithUpload(view, file, (currentView, node) => {
                const transaction = currentView.state.tr.insert(
                  coordinates.pos,
                  node
                );
                currentView.dispatch(transaction);
                return transaction.mapping.map(coordinates.pos);
              });
            }
            return true;
          }
        }
        return false;
      },
    },
  });

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const videoFileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!editor) return;
    const editable = isFybrAdmin && isEditing;
    editor.setEditable(editable);
    if (editable) {
      editor.commands.focus("end");
    }
  }, [editor, isFybrAdmin, isEditing]);

  React.useEffect(() => {
    if (!editor) return;

    if (isCreating) {
      editor.commands.setContent({
        type: "doc",
        content: [{ type: "paragraph" }],
      });
      return;
    }

    let cancelled = false;

    const loadContent = async () => {
      if (!article?.content) return;

      const urlMap = await resolveImageUrlsToSigned(article.content);
      const loaded = await preloadImages(Object.values(urlMap));
      const safeMap = Object.entries(urlMap).reduce<Record<string, string>>(
        (acc, [original, signed]) => {
          const isVideo =
            original.includes(KNOWLEDGE_BASE_VIDEO_PREFIX) ||
            signed.includes(KNOWLEDGE_BASE_VIDEO_PREFIX);
          if (isVideo || loaded.has(signed)) {
            acc[original] = signed;
          }
          return acc;
        },
        {}
      );

      const hydratedContent = replaceImageUrls(article.content, safeMap);

      if (!cancelled) {
        editor.commands.setContent(hydratedContent);
      }
    };

    loadContent();

    return () => {
      cancelled = true;
    };
  }, [editor, article, isCreating, isEditing]);

  const [linkPopoverOpen, setLinkPopoverOpen] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState("");

  type BubbleMenuShouldShowProps = {
    editor: Editor;
    element: HTMLElement;
    view: EditorView;
    state: EditorState;
    oldState?: EditorState;
    from: number;
    to: number;
  };

  const bubbleMenuShouldShow = React.useCallback(
    ({ editor: bubbleEditor, state }: BubbleMenuShouldShowProps) => {
      if (!bubbleEditor.isEditable) return false;
      if (linkPopoverOpen) return true;
      const { from, to } = state.selection;
      return from !== to;
    },
    [linkPopoverOpen]
  );

  React.useEffect(() => {
    if (!isEditing || !isFybrAdmin) {
      setLinkPopoverOpen(false);
    }
  }, [isEditing, isFybrAdmin]);

  const handleSetLink = () => {
    if (!editor) return;
    const previousUrl = editor.getAttributes("link").href as string | undefined;
    setLinkUrl(previousUrl || "https://");
    setLinkPopoverOpen(true);
  };

  const applyLink = () => {
    if (!editor) return;
    if (linkUrl === "") {
      editor.chain().focus().unsetLink().run();
    } else {
      editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: linkUrl })
        .run();
    }
    setLinkPopoverOpen(false);
    setLinkUrl("");
  };

  const handleRemoveLink = () => {
    if (!editor) return;
    editor.chain().focus().unsetLink().run();
    setLinkPopoverOpen(false);
    setLinkUrl("");
  };

  const handleCloseLinkPopover = () => {
    setLinkPopoverOpen(false);
    setLinkUrl("");
  };

  const handleSave = () => {
    if (!editor) return;

    const trimmedTitle = editTitle.trim();
    if (!trimmedTitle) {
      alert("Please enter a title");
      return;
    }

    const normalizedSlug = slugify(editSlug || trimmedTitle);
    onSave({
      title: trimmedTitle,
      slug: normalizedSlug,
      content: editor.getJSON(),
    });
  };

  const insertImage = (file: File) => {
    if (!editor) return;
    const { view } = editor;
    insertImageWithUpload(
      view,
      file,
      (currentView, blobUrl) => {
        const node = currentView.state.schema.nodes.image.create({
          src: blobUrl,
        });
        const { from } = currentView.state.selection;
        const transaction = currentView.state.tr.replaceSelectionWith(node);
        const insertedPos = transaction.mapping.map(from);
        currentView.dispatch(transaction);
        return insertedPos;
      },
      { postInsert: () => editor.commands.focus() }
    );
  };

  const insertVideo = (file: File) => {
    if (!editor) return;
    const { view } = editor;
    insertVideoWithUpload(
      view,
      file,
      (currentView, node) => {
        const { from } = currentView.state.selection;
        const transaction = currentView.state.tr.replaceSelectionWith(node);
        const insertedPos = transaction.mapping.map(from);
        currentView.dispatch(transaction);
        return insertedPos;
      },
      { postInsert: () => editor.commands.focus() }
    );
  };

  const handleImageFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      insertImage(file);
    }
    event.target.value = "";
  };

  const handleVideoFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith("video/")) {
      insertVideo(file);
    }
    event.target.value = "";
  };

  if (isLoading && !isCreating) {
    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
        `}
      >
        <Spinner size={40} intent="primary" />
      </div>
    );
  }

  if (!article && !isCreating) {
    return (
      <div
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: var(--neutral-4);
        `}
      >
        <Icon
          icon="document"
          size={60}
          css={css`
            margin-bottom: 1rem;
          `}
        />
        <p>
          {isFybrAdmin ? "Create the first article." : "No articles available."}
        </p>
      </div>
    );
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 1rem;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        `}
      >
        {isEditing ? (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 0.5rem;
              flex: 1;
              max-width: 600px;
            `}
          >
            <label
              css={css`
                display: flex;
                flex-direction: column;
                gap: 0.25rem;
              `}
            >
              <span
                css={css`
                  text-transform: uppercase;
                  font-size: 0.8em;
                  font-weight: 700;
                  color: var(--neutral-4);
                `}
              >
                Title
              </span>
              <input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="Article title"
                css={css`
                  font-size: 2em;
                  font-weight: 700;
                  color: var(--neutral-6);
                  background-color: var(--neutral-2);
                  border: none;
                  width: 100%;
                  padding: 0.5rem 0;
                `}
              />
            </label>
            {!isCreating && (
              <label
                css={css`
                  display: flex;
                  flex-direction: column;
                  gap: 0.25rem;
                `}
              >
                <span
                  css={css`
                    text-transform: uppercase;
                    font-size: 0.8em;
                    font-weight: 700;
                    color: var(--neutral-4);
                  `}
                >
                  Slug
                </span>
                <input
                  value={editSlug}
                  onChange={(e) => setEditSlug(slugify(e.target.value))}
                  placeholder="article-slug"
                  css={css`
                    font-size: 1em;
                    font-weight: 700;
                    color: var(--neutral-6);
                    background-color: var(--neutral-2);
                    border: none;
                    width: 100%;
                    padding: 0.5rem 0;
                  `}
                />
              </label>
            )}
          </div>
        ) : (
          <div>
            <h1
              css={css`
                margin: 0;
                color: var(--neutral-6);
                padding: 0.5rem 0;
              `}
            >
              {article.title}
            </h1>
          </div>
        )}

        {isFybrAdmin && (
          <div
            css={css`
              display: flex;
              gap: 0.5rem;
            `}
          >
            {isEditing ? (
              <>
                <Button
                  icon="tick"
                  text={isCreating ? "Create" : "Save"}
                  intent="success"
                  onClick={handleSave}
                  loading={isSaving}
                />
                <Button icon="cross" text="Cancel" onClick={onCancelEdit} />
              </>
            ) : (
              <>
                <Button icon="edit" text="Edit" onClick={onStartEdit} />
                <Button
                  icon="trash"
                  intent="danger"
                  onClick={onDelete}
                  loading={isDeleting}
                />
              </>
            )}
          </div>
        )}
      </div>
      <div
        css={css`
          flex: 1;

          & > div {
            height: 100%;
          }
        `}
        onClick={() => {
          editor?.commands.focus();
        }}
      >
        <BubbleMenu editor={editor} shouldShow={bubbleMenuShouldShow}>
          <ButtonGroup>
            <Button
              icon="bold"
              onClick={() => editor.chain().focus().toggleBold().run()}
            />
            <Button
              icon="italic"
              onClick={() => editor.chain().focus().toggleItalic().run()}
            />
            <Button
              icon="underline"
              onClick={() => editor.chain().focus().toggleUnderline().run()}
            />
            <Popover
              usePortal={false}
              isOpen={linkPopoverOpen}
              onClose={handleCloseLinkPopover}
              content={
                <div style={{ padding: "1rem", width: "300px" }}>
                  <FormGroup label="URL" labelFor="link-url">
                    <InputGroup
                      id="link-url"
                      value={linkUrl}
                      onChange={(e) => setLinkUrl(e.target.value)}
                      placeholder="https://example.com"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") applyLink();
                        if (e.key === "Escape") handleCloseLinkPopover();
                      }}
                    />
                  </FormGroup>
                  <div
                    style={{
                      marginTop: "0.5rem",
                      display: "flex",
                      gap: "0.5rem",
                    }}
                  >
                    <Button text="Apply" intent="primary" onClick={applyLink} />
                    <Button
                      icon="unlink"
                      text="Remove Link"
                      onClick={handleRemoveLink}
                    />
                    <Button text="Cancel" onClick={handleCloseLinkPopover} />
                  </div>
                </div>
              }
            >
              <Button icon="link" onClick={handleSetLink} />
            </Popover>
          </ButtonGroup>
        </BubbleMenu>
        <FloatingMenu editor={editor}>
          <ButtonGroup>
            <Button
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 2 }).run()
              }
            >
              H2
            </Button>
            <Button
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 3 }).run()
              }
            >
              H3
            </Button>
            <Button
              icon="media"
              onClick={() => fileInputRef.current?.click()}
            />
            <Button
              icon="video"
              onClick={() => videoFileInputRef.current?.click()}
            />
          </ButtonGroup>
        </FloatingMenu>
        <input
          type="file"
          ref={fileInputRef}
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleImageFileSelect}
        />
        <input
          type="file"
          ref={videoFileInputRef}
          accept="video/*"
          style={{ display: "none" }}
          onChange={handleVideoFileSelect}
        />
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default ArticleViewer;
